import { Menu, Clock, LogOut, DollarSign, LifeBuoy } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { VideoHistoryList } from "@/components/video/VideoHistoryList";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

interface MobileMenuProps {
  onSignOut: () => Promise<void>;
}

export const MobileMenu = ({ onSignOut }: MobileMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handlePricingClick = () => {
    setIsMobileMenuOpen(false);
    if (location.pathname === '/landing') {
      const element = document.getElementById('pricing');
      element?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate('/pricing');
    }
  };

  return (
    <div className="md:hidden">
      <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <SheetTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-10 w-10 hover:bg-primary/10"
          >
            <span className="sr-only">Open menu</span>
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent 
          side="right" 
          className="w-[280px] flex flex-col p-0 gap-0"
        >
          <SheetHeader className="p-6 border-b">
            <SheetTitle>Menu</SheetTitle>
          </SheetHeader>
          
          <div className="flex-1 p-6 space-y-4">
            <Collapsible 
              open={isOpen} 
              onOpenChange={setIsOpen}
              className="w-full space-y-2"
            >
              <CollapsibleTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="w-full gap-2 justify-start hover:bg-primary/10 min-h-[44px]"
                >
                  <Clock className="h-4 w-4" />
                  History
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="animate-accordion-down">
                <div className="mt-2 space-y-2 max-h-[60vh] overflow-y-auto pr-2">
                  <VideoHistoryList compact />
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>

          <div className="mt-auto p-6 space-y-3 border-t">
            <Button 
              onClick={handlePricingClick}
              variant="ghost"
              className="w-full gap-2 justify-start hover:bg-primary/10 min-h-[44px]"
            >
              <DollarSign className="h-4 w-4" />
              Pricing
            </Button>

            <Link 
              to="/support" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="block"
            >
              <Button 
                variant="ghost"
                className="w-full gap-2 justify-start hover:bg-primary/10 min-h-[44px]"
              >
                <LifeBuoy className="h-4 w-4" />
                Support
              </Button>
            </Link>

            <div className="pt-2 border-t">
              <Button 
                onClick={() => {
                  onSignOut();
                  setIsMobileMenuOpen(false);
                }} 
                variant="ghost" 
                className="w-full justify-start gap-2 hover:bg-primary/10 min-h-[44px]"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};